<!-- eslint-disable vue/no-v-html -->
<template>
  <SectionIntro
    :title="title"
    :body="description"
  />
  <div class="max-container">
    <div class="mt-8 flex flex-col lg:mr-auto max-w-7xl">
      <!-- Main Display Section -->
      <div
        class="w-full transition-opacity duration-200 border border-gray-200"
      >
        <PeopleCarouselItem
          v-model:current-index="currentIndex"
          v-model:autoplay="autoplay"
          :item="items[currentIndex]"
          :total-items="items.length"
          :progress-width="progressWidth"
        />
      </div>
      <!-- Thumbnails -->
      <div class="hidden xl:flex gap-px overflow-x-auto mt-4">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="{
            'active': currentIndex === index
          }"
          class="shrink-0 overflow-hidden cursor-pointer hover:grayscale-0 transition grayscale [&.active]:grayscale-0 [&.active]:pointer-events-none"
        >
          <button @click="currentIndex = index">
            <FocalImage
              v-if="item.image"
              :image="{
                url: item.image.thumbnail_url || item.image.url,
                width: item.image.width || item.image.width,
                height: item.image.height || item.image.height,
                focal_point: item.image.focal_point || item.image.focal_point,
                title: item.image.thumbnail_name || item.image.title,
                alt: item.image.thumbnail_alt || item.image.alt,
              }"
              img-class="aspect-video w-auto h-36"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeMount } from 'vue';
import type { ImageRendition } from '@/ts/types/focal-point';

interface PeopleCarouselItem {
  title: string;
  name: string;
  description: string;
  image: ImageRendition | null;
}

interface PeopleCarousel {
  uuid?: string;
  title?: string;
  description?: string;
  autoplay?: boolean;
  items?: PeopleCarouselItem[];
}

// Define props with type and default values using withDefaults
const props = withDefaults(defineProps<PeopleCarousel>(), {
  uuid: '',
  title: '',
  description: '',
  autoplay: false,
  items: () => []
});

const currentIndex = ref(0);
const autoplay = ref(props.autoplay);
const duration = 10 // seconds
const startTime = ref(null)
const elapsed = ref(0)
let animationFrameId = null
let restartTimeoutId = null

const progressWidth = computed(() => {
  return autoplay.value
    ? Math.min((elapsed.value / duration) * 100, 100)
    : 100 // full red bar when autoplay is off
})

const updateProgress = (timestamp) => {
  if (!startTime.value) startTime.value = timestamp
  const delta = (timestamp - startTime.value) / 1000
  elapsed.value = delta

  if (delta < duration) {
    animationFrameId = requestAnimationFrame(updateProgress)
  } else {
    cancelAnimationFrame(animationFrameId)
    if (autoplay.value) {
      restartTimeoutId = setTimeout(() => {
        currentIndex.value = (currentIndex.value + 1) % props.items.length;
        startTime.value = null
        elapsed.value = 0
        animationFrameId = requestAnimationFrame(updateProgress)
      }, 1000) // 1-second delay before restart
    }
  }
}

const startAnimation = () => {
  startTime.value = null
  elapsed.value = 0
  animationFrameId = requestAnimationFrame(updateProgress)
}

const rootElement = ref(null);

onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json']
});

watch(currentIndex, () => {
  startTime.value = null
  elapsed.value = 0
})

watch(autoplay, (newVal) => {
  if (newVal) {
    startAnimation()
  } else {
    cancelAnimationFrame(animationFrameId)
    clearTimeout(restartTimeoutId)
    elapsed.value = duration
  }
}, { immediate: true })
</script>
